import { useMemo, useState } from "react";
import { formatDateTime } from "@excellent/ui";
import { ApiError, apiCall, useApi } from "@excellent/ui/api";
import { productEnvironmentLabel, type VerificationEnvironment } from "../env";
import { useVerificationEnvOptional } from "../env-provider";
import {
  ActionButton,
  ButtonRow,
  Callout,
  DataTable,
  Mono,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
} from "../ui";
import { verificationCanonicalClients } from "../api/canonical-clients";
import { PageFrame, PagePending, type VerificationPageProps } from "./page-frame";

type CoverageRow = {
  surface: string;
  states: string[];
  expected: string[];
};

type TransitionRow = {
  step: string;
  before: string;
  after: string;
  status: "pass" | "fail";
  detail: string;
};

const EXPECTED = {
  controls: ["PASS", "FAIL", "UNKNOWN", "NOT_RUN"],
  policies: ["active", "draft", "retired"],
  policyIntents: ["tighten", "neutral", "loosen"],
  workflows: ["admitted", "draft", "needs-oracle", "reject"],
  evidence: ["active", "redacted", "deleted", "expired"],
  connectors: ["healthy", "stale", "blocked", "revoked", "paused"],
  runs: ["queued", "running", "completed", "failed", "blocked"],
  receipts: ["PASS", "FAIL", "UNKNOWN"],
  packs: ["audit_preparation", "grc_exchange", "offline_replay"],
  auditGrants: ["active", "revoked"],
  workers: ["healthy", "starting", "stopped", "failed"],
  metrics: ["pass", "warn", "fail", "blocked"],
  runtime: ["pass", "warn", "fail"],
  tokens: ["active", "revoked"],
} as const;

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The app transition audit did not complete.";
}

function actorId(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

function unique(values: (string | null | undefined)[]): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
}

function tokenState(token: { revokedAt: string | null }): string {
  return token.revokedAt ? "revoked" : "active";
}

function grantState(grant: { revokedAt: string | null }): string {
  return grant.revokedAt ? "revoked" : "active";
}

function coveragePass(row: CoverageRow): boolean {
  return row.expected.every((state) => row.states.includes(state));
}

function transition(step: string, before: string, after: string, detail: string): TransitionRow {
  return {
    step,
    before,
    after,
    status: before !== after ? "pass" : "fail",
    detail,
  };
}

function blockedTransition(step: string, before: string, err: unknown): TransitionRow {
  return {
    step,
    before,
    after: "blocked",
    status: "fail",
    detail: errorMessage(err),
  };
}

export function DemoLabPage(_props: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [transitions, setTransitions] = useState<TransitionRow[]>([]);

  const args = ready && environment ? { query: { env: environment } } : null;
  const overview = useApi(verificationCanonicalClients.overview.operation, args);
  const controls = useApi(verificationCanonicalClients.controlsList.operation, args);
  const policies = useApi(verificationCanonicalClients.policiesList.operation, args);
  const workflows = useApi(verificationCanonicalClients.workflowsList.operation, args);
  const evidence = useApi(verificationCanonicalClients.artifactsList.operation, args);
  const connectors = useApi(verificationCanonicalClients.connectorsList.operation, args);
  const runs = useApi(verificationCanonicalClients.runsList.operation, args);
  const receipts = useApi(verificationCanonicalClients.receiptsList.operation, args);
  const packs = useApi(verificationCanonicalClients.packsList.operation, args);
  const auditRoom = useApi(verificationCanonicalClients.auditRoomGet.operation, args);
  const workers = useApi(verificationCanonicalClients.workersList.operation, args);
  const scorecard = useApi(verificationCanonicalClients.scorecardGet.operation, args);
  const runtime = useApi(verificationCanonicalClients.runtimeGet.operation, args);
  const catalog = useApi(verificationCanonicalClients.catalogGet.operation, args);

  const apis = [
    overview,
    controls,
    policies,
    workflows,
    evidence,
    connectors,
    runs,
    receipts,
    packs,
    auditRoom,
    workers,
    scorecard,
    runtime,
    catalog,
  ];
  const firstError = apis.find((api) => api.error)?.error;
  const loading = apis.some((api) => api.isLoading || (!api.data && !api.error));

  const coverage = useMemo<CoverageRow[]>(() => {
    return [
      { surface: "controls", states: unique(controls.data?.items.map((row) => row.status) ?? []), expected: [...EXPECTED.controls] },
      { surface: "policies", states: unique(policies.data?.items.map((row) => row.status) ?? []), expected: [...EXPECTED.policies] },
      { surface: "policy intents", states: unique(policies.data?.items.map((row) => row.intent) ?? []), expected: [...EXPECTED.policyIntents] },
      { surface: "workflows", states: unique(workflows.data?.items.map((row) => row.status) ?? []), expected: [...EXPECTED.workflows] },
      { surface: "evidence", states: unique(evidence.data?.items.map((row) => row.state) ?? []), expected: [...EXPECTED.evidence] },
      { surface: "connectors", states: unique(connectors.data?.items.map((row) => row.health) ?? []), expected: [...EXPECTED.connectors] },
      { surface: "runs", states: unique(runs.data?.items.map((row) => row.lifecycle) ?? []), expected: [...EXPECTED.runs] },
      { surface: "receipts", states: unique(receipts.data?.items.map((row) => row.status) ?? []), expected: [...EXPECTED.receipts] },
      { surface: "packs", states: unique(packs.data?.items.map((row) => row.kind) ?? []), expected: [...EXPECTED.packs] },
      { surface: "audit grants", states: unique(auditRoom.data?.grants.map(grantState) ?? []), expected: [...EXPECTED.auditGrants] },
      { surface: "workers", states: unique(workers.data?.items.map((row) => row.status) ?? []), expected: [...EXPECTED.workers] },
      { surface: "scorecard", states: unique(scorecard.data?.metrics.map((row) => row.status) ?? []), expected: [...EXPECTED.metrics] },
      { surface: "runtime checks", states: unique(runtime.data?.checks.map((row) => row.status) ?? []), expected: [...EXPECTED.runtime] },
      { surface: "API tokens", states: unique(catalog.data?.tokens.map(tokenState) ?? []), expected: [...EXPECTED.tokens] },
    ];
  }, [auditRoom.data, catalog.data, connectors.data, controls.data, evidence.data, packs.data, policies.data, receipts.data, runs.data, runtime.data, scorecard.data, workflows.data, workers.data]);

  const coverageOk = coverage.every(coveragePass);
  const tenantName = overview.data?.tenant.name ?? productEnvironmentLabel(environment ?? "real");
  const pending = pendingAction !== null;
  const transitionSummary = transitions.map((row) => `${row.step}: ${row.after}`).join("; ");

  async function revalidateAll() {
    await overview.mutate();
    await controls.mutate();
    await policies.mutate();
    await workflows.mutate();
    await evidence.mutate();
    await connectors.mutate();
    await runs.mutate();
    await receipts.mutate();
    await packs.mutate();
    await auditRoom.mutate();
    await workers.mutate();
    await scorecard.mutate();
    await runtime.mutate();
    await catalog.mutate();
  }

  async function manageEnvironment(action: "spawn" | "reset") {
    if (!environment || pending) return;
    setPendingAction(action);
    setNotice(null);
    if (action === "reset") setTransitions([]);
    try {
      const result = await apiCall(
        action === "spawn"
          ? verificationCanonicalClients.localEnvironmentsSpawn.operation
          : verificationCanonicalClients.localEnvironmentsReset.operation,
        {
          query: { env: environment },
          body: { actorId: actorId(environment) },
        },
      );
      setNotice(`${action === "spawn" ? "Spawned" : "Reset"} ${tenantName}: ${result.recordCount} records, seed ${result.seedVersion}.`);
      await revalidateAll();
    } catch (err) {
      setNotice(errorMessage(err));
    } finally {
      setPendingAction(null);
    }
  }

  async function runTransitionAudit() {
    if (!environment || pending) return;
    setPendingAction("audit");
    setNotice(null);
    setTransitions([]);
    const actor = actorId(environment);
    const envQuery = { env: environment };
    const rows: TransitionRow[] = [];
    try {
      const draftPolicy = policies.data?.items.find((row) => row.status === "draft") ?? policies.data?.items[0];
      if (draftPolicy) {
        try {
          const before = draftPolicy.status;
          const activated = await apiCall(verificationCanonicalClients.policiesActivate.operation, {
            params: { id: draftPolicy.id },
            query: envQuery,
            body: { actorId: actor, rationale: "Local fixture transition audit" },
          });
          rows.push(transition("policy activation", before, activated.policy.status, activated.policy.id));
        } catch (err) {
          rows.push(blockedTransition("policy activation", draftPolicy.status, err));
        }
      }

      try {
        const createdWorkflow = await apiCall(verificationCanonicalClients.workflowsIntake.operation, {
          query: envQuery,
          body: {
            actorId: actor,
            name: `${environment === "demo" ? "Demo" : "Dogfood"} lab workflow ${new Date().toISOString()}`,
            actor: "verification-lab",
            doc: "A lab workflow must name an independent oracle before it can be admitted.",
            oracle: environment === "demo" ? "demo:src_jira" : "real:src_github",
            verifier: "Excellent verifier",
            evidence: "Connector record and signed receipt",
            consequence: "high",
          },
        });
        const admittedWorkflow = await apiCall(verificationCanonicalClients.workflowsAdmit.operation, {
          params: { id: createdWorkflow.workflow.id },
          query: envQuery,
          body: { actorId: actor },
        });
        rows.push(transition("workflow admission", createdWorkflow.workflow.status, admittedWorkflow.workflow.status, admittedWorkflow.workflow.id));
      } catch (err) {
        rows.push(blockedTransition("workflow admission", "intake", err));
      }

      const activeArtifact = evidence.data?.items.find((row) => row.state === "active" && row.citedByReceiptIds.length === 0);
      if (activeArtifact) {
        try {
          const queued = await apiCall(verificationCanonicalClients.artifactsQueue.operation, {
            params: { id: activeArtifact.id },
            query: envQuery,
            body: { actorId: actor },
          });
          rows.push(transition("evidence queue", activeArtifact.collectionQueuedAt ? "queued" : "not queued", queued.artifact.collectionQueuedAt ? "queued" : "not queued", queued.artifact.id));
          const redacted = await apiCall(verificationCanonicalClients.artifactsRedact.operation, {
            params: { id: activeArtifact.id },
            query: envQuery,
            body: { actorId: actor, rationale: "Local fixture redaction transition" },
          });
          rows.push(transition("evidence redaction", activeArtifact.state, redacted.artifact.state, redacted.artifact.id));
        } catch (err) {
          rows.push(blockedTransition("evidence lifecycle", activeArtifact.state, err));
        }
      }

      const unhealthyConnector = connectors.data?.items.find((row) => row.health !== "healthy");
      if (unhealthyConnector) {
        try {
          const reconnected = await apiCall(verificationCanonicalClients.connectorsReconnect.operation, {
            params: { id: unhealthyConnector.id },
            query: envQuery,
            body: { actorId: actor },
          });
          rows.push(transition("connector reconnect", unhealthyConnector.health, reconnected.connector.health, reconnected.connector.id));
        } catch (err) {
          rows.push(blockedTransition("connector reconnect", unhealthyConnector.health, err));
        }
      }

      const completedRunIds = (runs.data?.items ?? []).filter((row) => row.lifecycle === "completed").map((row) => row.id);
      if (completedRunIds.length > 0) {
        try {
          const composed = await apiCall(verificationCanonicalClients.packsCompose.operation, {
            query: envQuery,
            body: {
              actorId: actor,
              label: `${productEnvironmentLabel(environment)} fixture pack ${new Date().toISOString()}`,
              kind: "offline_replay",
              runIds: completedRunIds,
            },
          });
          rows.push(transition("pack compose", "missing", composed.pack.kind, composed.pack.id));
          const granted = await apiCall(verificationCanonicalClients.auditRoomGrant.operation, {
            query: envQuery,
            body: {
              actorId: actor,
              reviewerActorId: environment === "demo" ? "demo:act_ext" : "real:act_auditor",
              packId: composed.pack.id,
              expiresAt: new Date(Date.now() + 86_400_000).toISOString(),
            },
          });
          rows.push(transition("audit grant", "missing", grantState(granted.grant), granted.grant.id));
          const revoked = await apiCall(verificationCanonicalClients.auditRoomRevoke.operation, {
            params: { id: granted.grant.id },
            query: envQuery,
            body: { actorId: actor },
          });
          rows.push(transition("audit revoke", grantState(granted.grant), grantState(revoked.grant), revoked.grant.id));
        } catch (err) {
          rows.push(blockedTransition("pack and audit-room", "missing", err));
        }
      }

      try {
        const createdToken = await apiCall(verificationCanonicalClients.tokensCreate.operation, {
          query: envQuery,
          body: {
            actorId: actor,
            label: `${productEnvironmentLabel(environment)} fixture token ${new Date().toISOString()}`,
            scopes: ["verification:read"],
          },
        });
        rows.push(transition("API token create", "missing", tokenState(createdToken.token), createdToken.token.id));
        const revokedToken = await apiCall(verificationCanonicalClients.tokensRevoke.operation, {
          params: { id: createdToken.token.id },
          query: envQuery,
          body: { actorId: actor },
        });
        rows.push(transition("API token revoke", tokenState(createdToken.token), tokenState(revokedToken.token), revokedToken.token.id));
      } catch (err) {
        rows.push(blockedTransition("API token lifecycle", "missing", err));
      }

      setTransitions(rows);
      setNotice(`Transition audit complete for ${tenantName}.`);
      await revalidateAll();
    } catch (err) {
      setTransitions(rows);
      setNotice(errorMessage(err));
    } finally {
      setPendingAction(null);
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="demo-lab" wide title="Local fixture lab" lede="Run local verification states through the product UI.">
        <PagePending title="Loading environment">Loading the current Environment fixture.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="demo-lab" wide title="Local fixture lab" lede="Run local verification states through the product UI.">
        <PagePending title="Environment unavailable">The lab mutates only the active Environment fixture.</PagePending>
      </PageFrame>
    );
  }

  if (firstError) {
    return (
      <PageFrame page="demo-lab" wide title="Local fixture lab" lede="Run local verification states through the product UI.">
        <PagePending title="Local fixture lab failed to load">{errorMessage(firstError)}</PagePending>
      </PageFrame>
    );
  }

  if (loading) {
    return (
      <PageFrame page="demo-lab" wide title="Local fixture lab" lede="Run local verification states through the product UI.">
        <PagePending title="Loading local fixture lab">Reading every verification contract for {productEnvironmentLabel(environment)}.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="demo-lab"
      wide
      title="Local fixture lab"
      lede="Spawned local fixture data must be acted on inside Excellent. This page is the product-owned permutation and transition harness."
      actions={
        <ButtonRow>
          <ActionButton
            actionId="demo-lab.spawn-environment"
            disabled={pending}
            onClick={() => void manageEnvironment("spawn")}
          >
            {pendingAction === "spawn" ? "Spawning" : "Spawn environment"}
          </ActionButton>
          <ActionButton
            actionId="demo-lab.reset-environment"
            disabled={pending}
            onClick={() => void manageEnvironment("reset")}
          >
            {pendingAction === "reset" ? "Resetting" : "Reset environment"}
          </ActionButton>
          <ActionButton
            actionId="demo-lab.run-transition-audit"
            variant="primary"
            disabled={pending}
            onClick={runTransitionAudit}
          >
            {pendingAction === "audit" ? "Running audit" : "Run transition audit"}
          </ActionButton>
        </ButtonRow>
      }
    >
      <Callout tone={coverageOk ? "pass" : "unknown"} title={`${tenantName} coverage ${coverageOk ? "complete" : "incomplete"}`}>
        Environment: <Mono>{environment}</Mono>. Generated at {overview.data ? formatDateTime(overview.data.tenant.periodEnd) : "unknown"}.
      </Callout>
      {notice ? <Callout tone={notice.includes("failed") || notice.includes("did not") ? "fail" : "accent"} title="Transition result">{notice}</Callout> : null}
      <StatGrid cols={4}>
        <Stat label="Coverage surfaces" value={`${coverage.filter(coveragePass).length}/${coverage.length}`} tone={coverageOk ? "pass" : "unknown"} />
        <Stat label="Controls" value={controls.data?.items.length ?? 0} />
        <Stat label="Runs" value={runs.data?.items.length ?? 0} />
        <Stat label="Transitions" value={transitions.length} tone={transitions.every((row) => row.status === "pass") ? "pass" : "unknown"} />
      </StatGrid>
      {transitions.length > 0 ? (
        <Callout tone={transitions.every((row) => row.status === "pass") ? "pass" : "unknown"} title="Transition checkpoints">
          {transitionSummary}
        </Callout>
      ) : null}
      <Section title="Permutation coverage" note="Every row is read from the active product contracts for the selected environment.">
        <DataTable
          title="State matrix"
          rows={coverage}
          rowKey={(row) => row.surface}
          columns={[
            { key: "surface", header: "Surface", render: (row) => row.surface },
            {
              key: "state",
              header: "Observed states",
              render: (row) => row.states.join(", "),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <ToneBadge tone={coveragePass(row) ? "pass" : "unknown"}>{coveragePass(row) ? "covered" : "missing"}</ToneBadge>,
            },
          ]}
        />
      </Section>
      <Section title="Transition audit" note="The button above mutates via apiCall against the same contracts used by the application pages.">
        {transitions.length === 0 ? (
          <Callout tone="unknown" title="No transition audit yet">
            Run the audit to exercise create, admit, activate, queue, redact, reconnect, compose, grant, revoke, mint, and revoke-token transitions.
          </Callout>
        ) : (
          <DataTable
            title="Transition ledger"
            rows={transitions}
            rowKey={(row) => `${row.step}:${row.detail}`}
            columns={[
              { key: "step", header: "Step", render: (row) => row.step },
              { key: "before", header: "Before", render: (row) => row.before },
              { key: "after", header: "After", render: (row) => row.after },
              {
                key: "status",
                header: "Status",
                render: (row) => <ToneBadge tone={row.status === "pass" ? "pass" : "fail"}>{row.status}</ToneBadge>,
              },
              { key: "detail", header: "Record", render: (row) => <Mono>{row.detail}</Mono> },
            ]}
          />
        )}
      </Section>
      <ButtonRow>
        <ActionButton actionId="demo-lab.refresh" onClick={revalidateAll} disabled={pending}>
          Refresh lab
        </ActionButton>
      </ButtonRow>
    </PageFrame>
  );
}
